(() => {
  'use strict';

  const scriptUrl = document.currentScript?.src || '';
  const assetBaseUrl = scriptUrl ? new URL('.', scriptUrl).href : '';
  const storyVideoAssets = [
    'hair-growth-story-1.mp4',
    'hair-growth-exact-2e69e8602fff85bf.mp4',
    'hair-growth-story-3.mp4',
    'hair-growth-story-4.mp4',
    'hair-growth-story-5.mp4',
  ];

  const SELECTORS = {
    page: '[data-hair-growth-exact]',
    root: '#replo-fullpage-element',
    product: '.r-mlh1px',
  };

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  function initializeCountdown(page) {
    const hours = page.querySelector('.r-1v0gyqt');
    const minutes = page.querySelector('.r-e5gp7i');
    const seconds = page.querySelector('.r-61gyom');
    if (!hours || !minutes || !seconds) return;

    const finishAt = Date.now() + 60 * 60 * 1000;
    const render = () => {
      let remaining = Math.max(0, finishAt - Date.now());
      const totalSeconds = Math.ceil(remaining / 1000);
      const hourValue = Math.floor(totalSeconds / 3600);
      const minuteValue = Math.floor((totalSeconds % 3600) / 60);
      const secondValue = totalSeconds % 60;
      hours.textContent = String(hourValue).padStart(2, '0');
      minutes.textContent = String(minuteValue).padStart(2, '0');
      seconds.textContent = String(secondValue).padStart(2, '0');
    };

    render();
    window.setInterval(render, 1000);
  }

  function initializeMarquee(page) {
    page.querySelectorAll('[data-replo-marquee-root="true"]').forEach((marquee) => {
      const track = marquee.querySelector('[data-replo-marquee-track="true"]');
      const sourceGroup = track?.firstElementChild;
      if (!track || !sourceGroup || marquee.dataset.hairGrowthMarqueeReady === 'true') return;
      marquee.dataset.hairGrowthMarqueeReady = 'true';

      const sourceTemplate = sourceGroup.cloneNode(true);
      let frame = 0;

      const rebuild = () => {
        frame = 0;
        track.classList.remove('hair-growth-marquee-track--ready');
        track.style.transform = 'translate3d(0, 0, 0)';
        while (sourceGroup.nextElementSibling) sourceGroup.nextElementSibling.remove();

        const groupWidth = sourceGroup.getBoundingClientRect().width;
        const viewportWidth = marquee.getBoundingClientRect().width;
        if (groupWidth <= 0 || viewportWidth <= 0) {
          frame = window.requestAnimationFrame(rebuild);
          return;
        }

        const copies = Math.max(3, Math.ceil(viewportWidth / groupWidth) + 2);
        for (let index = 1; index < copies; index += 1) {
          const clone = sourceTemplate.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          track.appendChild(clone);
        }

        track.style.setProperty('--hair-growth-marquee-shift', `${-groupWidth}px`);
        window.requestAnimationFrame(() => track.classList.add('hair-growth-marquee-track--ready'));
      };

      const scheduleRebuild = () => {
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(rebuild);
      };

      const resizeObserver = new ResizeObserver(scheduleRebuild);
      resizeObserver.observe(marquee);
      rebuild();
      document.fonts?.ready?.then(scheduleRebuild).catch(() => {});
    });
  }

  function initializeGallery(page) {
    const gallery = page.querySelector('#MediaGallery-main-product');
    const mediaList = gallery?.querySelector('#Slider-Gallery-main-product');
    if (!gallery || !mediaList || gallery.dataset.hairGrowthGalleryReady === 'true') return;

    const slides = [...mediaList.children].filter((element) => element.matches('.product__media-item'));
    const thumbnails = [...gallery.querySelectorAll('button.thumbnail')];
    if (!slides.length) return;

    gallery.dataset.hairGrowthGalleryReady = 'true';
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const stopNativeGalleryEvent = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    const select = (nextIndex, behavior = 'smooth') => {
      activeIndex = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-current', active ? 'true' : 'false');
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.toggleAttribute('inert', !active);
        slide.hidden = !active;
        if (active) {
          const image = slide.querySelector('img');
          if (image) image.loading = 'eager';
        }
      });
      thumbnails.forEach((thumbnail, index) => {
        const active = index === activeIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
      });

      mediaList.scrollLeft = 0;

      const thumbnail = thumbnails[activeIndex];
      const thumbnailList = thumbnail?.closest('ul');
      if (thumbnail && thumbnailList) {
        thumbnailList.scrollTo({ left: thumbnail.offsetLeft - thumbnailList.offsetLeft, behavior });
      }
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', (event) => {
        stopNativeGalleryEvent(event);
        select(index);
      }, true);
    });

    gallery.querySelectorAll('button[aria-label="Slide left"]').forEach((button) => {
      button.addEventListener('click', (event) => {
        stopNativeGalleryEvent(event);
        if (button.closest('#GalleryThumbnails-main-product')) {
          gallery.querySelector('#Slider-Thumbnails-main-product')?.scrollBy({ left: -220, behavior: 'smooth' });
          return;
        }
        select(activeIndex - 1);
      }, true);
    });

    gallery.querySelectorAll('button[aria-label="Slide right"]').forEach((button) => {
      button.addEventListener('click', (event) => {
        stopNativeGalleryEvent(event);
        if (button.closest('#GalleryThumbnails-main-product')) {
          gallery.querySelector('#Slider-Thumbnails-main-product')?.scrollBy({ left: 220, behavior: 'smooth' });
          return;
        }
        select(activeIndex + 1);
      }, true);
    });

    let pointerStart = null;
    mediaList.addEventListener('pointerdown', (event) => {
      pointerStart = event.clientX;
    }, { passive: true });
    mediaList.addEventListener('pointerup', (event) => {
      if (pointerStart === null) return;
      const distance = event.clientX - pointerStart;
      pointerStart = null;
      if (Math.abs(distance) < 45) return;
      select(distance > 0 ? activeIndex - 1 : activeIndex + 1);
    });

    select(activeIndex, 'instant');
  }

  function initializeAccordions(page) {
    page.querySelectorAll('[data-replo-collapsible="true"]').forEach((collapsible) => {
      const trigger = collapsible.querySelector(':scope > [role="button"][aria-controls]');
      const panelId = trigger?.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!trigger || !panel) return;

      const setExpanded = (expanded) => {
        trigger.setAttribute('aria-expanded', String(expanded));
        collapsible.dataset.state = expanded ? 'open' : 'closed';
        panel.hidden = !expanded;
        const verticalLine = [...trigger.querySelectorAll('svg line')].find(
          (line) => line.getAttribute('x1') === line.getAttribute('x2'),
        );
        if (verticalLine) verticalLine.style.display = expanded ? 'none' : '';
      };

      const toggle = (event) => {
        event.preventDefault();
        setExpanded(trigger.getAttribute('aria-expanded') !== 'true');
      };

      trigger.addEventListener('click', toggle);
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') toggle(event);
      });
      setExpanded(trigger.getAttribute('aria-expanded') === 'true');
    });
  }

  function initializeCarousel(carousel) {
    const viewport = carousel.querySelector('[data-replo-part="slides"]');
    const track = viewport?.querySelector(':scope > [data-replo-part="slide-track"]');
    if (!viewport || !track) return;

    const slides = [...track.children].filter((element) => element.matches('[data-replo-part="slide"]'));
    if (!slides.length) return;

    const sourceCount = Math.max(
      1,
      ...slides.map((slide) => Number(slide.getAttribute('data-slide-source-index')) + 1 || 1),
    );
    let activeIndex = 0;
    let maximumIndex = sourceCount - 1;

    const dimensions = () => {
      const first = slides[0];
      const second = slides[1];
      const firstLeft = first.offsetLeft;
      const step = second ? second.offsetLeft - firstLeft : first.getBoundingClientRect().width;
      const safeStep = Math.max(1, step);
      const visible = Math.max(1, Math.floor((viewport.clientWidth + 1) / safeStep));
      maximumIndex = Math.max(0, sourceCount - visible);
      return { firstLeft, step: safeStep };
    };

    const render = (animate = true) => {
      const { firstLeft, step } = dimensions();
      activeIndex = clamp(activeIndex, 0, maximumIndex);
      const target = slides.find(
        (slide) => Number(slide.getAttribute('data-slide-source-index')) === activeIndex,
      ) || slides[activeIndex];
      const offset = target ? target.offsetLeft - firstLeft : activeIndex * step;
      track.style.transitionProperty = 'transform';
      track.style.transitionDuration = animate ? '420ms' : '0ms';
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      slides.forEach((slide) => {
        const active = Number(slide.getAttribute('data-slide-source-index')) === activeIndex;
        slide.toggleAttribute('data-is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      carousel.querySelectorAll('[aria-label^="Go to slide"]').forEach((dot, index) => {
        const active = index === activeIndex;
        dot.setAttribute('aria-current', String(active));
        dot.classList.toggle('r-1ejhf44', active);
      });
      const live = carousel.querySelector('[aria-live]');
      if (live) live.textContent = `Slide ${activeIndex + 1} of ${sourceCount}`;
    };

    carousel.querySelectorAll('[data-replo-part="nav-button"]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const direction = button.getAttribute('data-direction');
        if (direction === 'previous') {
          activeIndex = activeIndex <= 0 ? maximumIndex : activeIndex - 1;
        } else {
          activeIndex = activeIndex >= maximumIndex ? 0 : activeIndex + 1;
        }
        render();
      });
    });

    carousel.querySelectorAll('[aria-label^="Go to slide"]').forEach((dot, index) => {
      const activate = (event) => {
        event.preventDefault();
        activeIndex = clamp(index, 0, maximumIndex);
        render();
      };
      dot.addEventListener('click', activate);
      dot.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') activate(event);
      });
    });

    const resizeObserver = new ResizeObserver(() => render(false));
    resizeObserver.observe(viewport);
    render(false);
  }

  function initializeCarousels(page) {
    page.querySelectorAll('[data-replo-carousel="true"]').forEach(initializeCarousel);
  }

  function initializeVideos(page) {
    const playButtons = [...page.querySelectorAll('[role="button"]')].filter((button) =>
      button.querySelector('svg[aria-label="Play"]'),
    );

    playButtons.forEach((button) => {
      const card = button.closest('[data-replo-part="slide"]');
      const video = card?.querySelector('video');
      if (!video || button.dataset.hairGrowthVideoReady === 'true') return;

      const sourceIndex = Number(card.getAttribute('data-slide-source-index'));
      const assetName = storyVideoAssets[sourceIndex];
      if (!video.getAttribute('src') && assetBaseUrl && assetName) {
        video.src = new URL(assetName, assetBaseUrl).href;
      }
      if (!video.getAttribute('src')) return;

      button.dataset.hairGrowthVideoReady = 'true';
      button.setAttribute('aria-label', `Play customer story ${sourceIndex + 1}`);
      video.preload = 'metadata';
      video.playsInline = true;

      const mediaWrap = video.parentElement;
      const posterImage = mediaWrap?.querySelector('img');
      const buttonWrap = button.parentElement;
      const restorePoster = () => {
        video.pause();
        video.controls = false;
        video.classList.remove('is-playing');
        posterImage?.removeAttribute('hidden');
        if (buttonWrap) buttonWrap.hidden = false;
      };
      const play = async (event) => {
        event.preventDefault();
        page.querySelectorAll('video.is-playing').forEach((otherVideo) => {
          if (otherVideo !== video) {
            otherVideo.dispatchEvent(new CustomEvent('hair-growth:stop'));
          }
        });
        posterImage?.setAttribute('hidden', '');
        if (buttonWrap) buttonWrap.hidden = true;
        video.controls = true;
        video.classList.add('is-playing');
        try {
          await video.play();
        } catch (_) {
          restorePoster();
        }
      };

      button.addEventListener('click', play);
      button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') play(event);
      });
      video.addEventListener('ended', restorePoster);
      video.addEventListener('error', restorePoster);
      video.addEventListener('hair-growth:stop', restorePoster);
    });
  }

  function initializeTimeline(page) {
    page.querySelectorAll('.timeline-fixed-v2').forEach((timeline) => {
      if (timeline.dataset.hairGrowthTimelineReady === 'true') return;

      const line = timeline.querySelector('.tfv2-line');
      const fill = timeline.querySelector('.tfv2-line-fill');
      const items = [...timeline.querySelectorAll('.tfv2-item')];
      const bullets = [...timeline.querySelectorAll('.tfv2-item li')];
      if (!line || !fill || !items.length) return;

      timeline.dataset.hairGrowthTimelineReady = 'true';
      let frame = 0;

      const update = () => {
        frame = 0;
        const activationY = window.innerHeight * 0.55;
        const lineRect = line.getBoundingClientRect();
        const progress = clamp(activationY - lineRect.top, 0, lineRect.height);
        fill.style.height = `${progress}px`;

        items.forEach((item) => {
          item.classList.toggle('active', item.getBoundingClientRect().top <= activationY);
        });
        bullets.forEach((bullet) => {
          bullet.classList.toggle('active', bullet.getBoundingClientRect().top <= activationY);
        });
      };

      const scheduleUpdate = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      };

      const scrollContainer = page.closest('.page-wrapper');
      window.addEventListener('scroll', scheduleUpdate, { passive: true });
      window.addEventListener('resize', scheduleUpdate, { passive: true });
      if (scrollContainer) scrollContainer.addEventListener('scroll', scheduleUpdate, { passive: true });
      window.requestAnimationFrame(update);
    });
  }

  function readVariants(page) {
    const data = page.querySelector('[data-hair-growth-variants]')?.textContent || '[]';
    try {
      return JSON.parse(data);
    } catch (_) {
      return [];
    }
  }

  function initializeProductForm(page) {
    const bars = [...page.querySelectorAll('.kaching-bundles__bar[data-deal-bar-id]')];
    const variants = readVariants(page);
    const nativeVariant = page.querySelector('[data-hair-growth-native-variant]');
    const nativeQuantity = page.querySelector('[data-hair-growth-native-quantity]');
    const stickyButton = page.querySelector('[data-hair-growth-sticky-cart]');
    const status = page.querySelector('[data-hair-growth-add-status]');
    let selectedIndex = Math.max(0, bars.findIndex((bar) => bar.classList.contains('kaching-bundles__bar--selected')));
    let statusTimer;

    const normalizedTitle = (variant) => `${variant.title || ''} ${variant.name || ''}`.toLowerCase();
    const matchVariant = (index) => {
      const patterns = [
        /buy\s*1\s*\+\s*1|buy\s*1.*1\s*free/,
        /buy\s*2\s*\+\s*2|buy\s*2.*2\s*free/,
        /buy\s*3\s*\+\s*3|buy\s*3.*3\s*free/,
      ];
      const byTitle = variants.find((variant) => patterns[index]?.test(normalizedTitle(variant)) && variant.available !== false);
      if (byTitle) return byTitle;
      const offset = variants.length >= 4 ? 1 : 0;
      return variants[offset + index] || variants[index] || variants.find((variant) => variant.available !== false) || variants[0];
    };

    const selectedVariant = () => matchVariant(selectedIndex);

    const synchronizeVariant = () => {
      const variant = selectedVariant();
      if (!variant) return;
      if (nativeVariant) nativeVariant.value = String(variant.id);
      if (nativeQuantity) nativeQuantity.value = String((selectedIndex + 1) * 2);
      page.querySelectorAll(`${SELECTORS.root} form[action$="/cart/add"] input[name="id"]`).forEach((input) => {
        input.value = String(variant.id);
      });
    };

    const chooseBar = (index) => {
      selectedIndex = clamp(index, 0, Math.max(0, bars.length - 1));
      bars.forEach((bar, barIndex) => {
        const selected = barIndex === selectedIndex;
        bar.classList.toggle('kaching-bundles__bar--selected', selected);
        const radio = bar.querySelector('input[type="radio"]');
        if (radio) radio.checked = selected;
      });
      synchronizeVariant();
    };

    bars.forEach((bar, index) => {
      const activate = (event) => {
        event.preventDefault();
        chooseBar(index);
      };
      bar.addEventListener('click', activate);
      bar.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') activate(event);
      });
    });

    const announce = (message, duration = 2200) => {
      if (!status) return;
      window.clearTimeout(statusTimer);
      status.textContent = message;
      status.classList.add('is-visible');
      statusTimer = window.setTimeout(() => status.classList.remove('is-visible'), duration);
    };

    page.querySelectorAll(`${SELECTORS.root} form[action$="/cart/add"]`).forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (form.dataset.hairGrowthCheckoutPending === 'true') return;

        synchronizeVariant();

        const visibleButton = form.querySelector('[type="submit"]');
        const purchaseButtons = [visibleButton, stickyButton].filter(Boolean);
        const setBusy = (busy) => {
          purchaseButtons.forEach((button) => {
            button.disabled = busy;
            button.toggleAttribute('aria-busy', busy);
          });
        };

        const variant = selectedVariant();
        if (!variant) {
          announce('Select an available product option');
          return;
        }

        const quantity = Math.max(1, Number.parseInt(nativeQuantity?.value || '1', 10) || 1);
        const shopRoot = window.Shopify?.routes?.root || '/';
        const addUrl = `${shopRoot}cart/add.js`;
        const checkoutUrl = `${shopRoot}checkout`;

        form.dataset.hairGrowthCheckoutPending = 'true';
        setBusy(true);
        announce('Adding to cart…');

        try {
          const response = await window.fetch(addUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: [{ id: variant.id, quantity }],
            }),
          });

          if (!response.ok) {
            let message = 'Unable to add this offer. Please try again.';
            try {
              const error = await response.json();
              message = error.description || error.message || message;
            } catch (_) {
              // Keep the fallback message when Shopify does not return JSON.
            }
            throw new Error(message);
          }

          announce('Redirecting to checkout…');
          window.location.assign(checkoutUrl);
        } catch (error) {
          form.dataset.hairGrowthCheckoutPending = 'false';
          setBusy(false);
          announce(error instanceof Error ? error.message : 'Unable to add this offer. Please try again.');
        }
      }, true);
    });

    chooseBar(selectedIndex);
  }

  function initializeCallsToAction(page) {
    const target = page.querySelector(SELECTORS.product);
    if (!target) return;
    page.querySelectorAll('button[role="link"]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function initializeStickyCart(page) {
    const stickyButton = page.querySelector('[data-hair-growth-sticky-cart]');
    const sourceButton = page.querySelector('.main-product-atc');
    const sourceForm = sourceButton?.closest('form');
    if (!stickyButton || !sourceButton || !sourceForm || stickyButton.dataset.hairGrowthStickyReady === 'true') return;

    stickyButton.dataset.hairGrowthStickyReady = 'true';
    const scrollContainer = page.closest('.page-wrapper');
    let activationPoint = 0;
    let frame = 0;

    const currentScrollTop = () => Math.max(
      window.scrollY || document.documentElement.scrollTop || 0,
      scrollContainer?.scrollTop || 0,
    );

    const measure = () => {
      activationPoint = sourceButton.getBoundingClientRect().bottom + currentScrollTop();
    };

    const update = () => {
      frame = 0;
      const bottom = Math.min(0, currentScrollTop() - activationPoint);
      stickyButton.style.bottom = `${bottom}px`;
      const exposed = bottom > -stickyButton.offsetHeight;
      stickyButton.setAttribute('aria-hidden', String(!exposed));
      stickyButton.tabIndex = exposed ? 0 : -1;
      stickyButton.disabled = sourceButton.disabled;
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    stickyButton.addEventListener('click', (event) => {
      event.preventDefault();
      sourceForm.requestSubmit(sourceButton);
    });

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    if (scrollContainer) scrollContainer.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', () => {
      measure();
      scheduleUpdate();
    }, { passive: true });

    measure();
    update();
  }

  function initializePage(page) {
    if (page.dataset.hairGrowthInitialized === 'true') return;
    page.dataset.hairGrowthInitialized = 'true';
    initializeCountdown(page);
    initializeMarquee(page);
    initializeGallery(page);
    initializeAccordions(page);
    initializeCarousels(page);
    initializeVideos(page);
    initializeTimeline(page);
    initializeProductForm(page);
    initializeCallsToAction(page);
    initializeStickyCart(page);
  }

  function initializeAll(scope = document) {
    if (scope.matches?.(SELECTORS.page)) initializePage(scope);
    scope.querySelectorAll?.(SELECTORS.page).forEach(initializePage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
